import React, { useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { isPublicPath } from './utils';
import { LoadingExperience } from './components';

const AttachmentsPage = React.lazy(() =>
  import('./pages/Attachments').then(module => ({
    default: module.AttachmentsPage,
  }))
);
const HeatPage = React.lazy(() =>
  import('./pages/Heat').then(module => ({ default: module.HeatPage }))
);
const HomePage = React.lazy(() =>
  import('./pages/Home').then(module => ({ default: module.HomePage }))
);
const MapPage = React.lazy(() =>
  import('./pages/Map').then(module => ({ default: module.MapPage }))
);
const StatsPage = React.lazy(() =>
  import('./pages/Stats').then(module => ({ default: module.StatsPage }))
);
const LoginPage = React.lazy(() =>
  import('./pages/Login').then(module => ({ default: module.LoginPage }))
);
const ChartPage = React.lazy(() =>
  import('./pages/Chart').then(module => ({ default: module.ChartPage }))
);
const MessagePage = React.lazy(() =>
  import('./pages/Message').then(module => ({ default: module.MessagePage }))
);
const WordCountMessageListPage = React.lazy(() =>
  import('./pages/Message/WordCountMessageList').then(module => ({
    default: module.WordCountMessageListPage,
  }))
);
const DateMessageListPage = React.lazy(() =>
  import('./pages/Message/DateMessageList').then(module => ({
    default: module.DateMessageListPage,
  }))
);
const SubjectMessageListPage = React.lazy(() =>
  import('./pages/Message/SubjectMessageList').then(module => ({
    default: module.SubjectMessageListPage,
  }))
);
const ChartTODPage = React.lazy(() =>
  import('./pages/ChartTOD').then(module => ({ default: module.ChartTODPage }))
);
const InfluenceGraph = React.lazy(() => import('./pages/Influence'));
const MessageTree = React.lazy(() => import('./pages/Message/MessageTree'));
const AddressBookPage = React.lazy(() =>
  import('./pages/AddressBook').then(module => ({
    default: module.AddressBookPage,
  }))
);
const AdminUserListPage = React.lazy(() =>
  import('./pages/Admin').then(module => ({ default: module.AdminUserList }))
);
const OhSnapPage = React.lazy(() =>
  import('./pages/OhSnap').then(module => ({ default: module.OhSnapPage }))
);

export const AppRoutes = () => {
  const location = useLocation();
  const currentPath = location.pathname;

  const token = localStorage.getItem('loginToken');
  const showMenu = token && token.length > 0;

  // The ohsnap365 archive is public — no login required
  const isPublic = isPublicPath(currentPath);

  useEffect(() => {
    if (
      (!showMenu && !isPublic && currentPath !== '/login') ||
      currentPath === '/logout'
    ) {
      localStorage.removeItem('loginToken');
      if (currentPath !== '/logout' && currentPath !== '/login') {
        localStorage.setItem('pre_login_path', currentPath);
      } else {
        localStorage.setItem('pre_login_path', '/stats/allTimeTopPosters');
      }
      window.location.href = '/login';
    }
  }, [showMenu, isPublic, currentPath]);

  if (!showMenu && !isPublic && currentPath !== '/login') {
    return <LoadingExperience />;
  }

  return (
    <React.Suspense fallback={<LoadingExperience />}>
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
        style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
      >
        <Routes>
          <Route path='/' element={<HomePage />} />
          <Route path='/stats' element={<StatsPage />} />
          <Route path='/stats/:which/:word' element={<StatsPage />} />
          <Route path='/stats/:which' element={<StatsPage />} />
          <Route
            path='/stats/word_count/:word/:name'
            element={<WordCountMessageListPage />}
          />
          <Route path='/message/:id' element={<MessagePage />} />
          <Route
            path='/messages_on/:date/:name'
            element={<DateMessageListPage />}
          />
          <Route path='/messages_on/:date' element={<DateMessageListPage />} />
          <Route path='/subject/:subject' element={<SubjectMessageListPage />} />
          <Route path='/heat' element={<HeatPage />} />
          <Route path='/heat/:who' element={<HeatPage />} />
          <Route path='/map' element={<MapPage />} />
          <Route path='/chart' element={<ChartPage />} />
          <Route path='/time_of_day' element={<ChartTODPage />} />
          <Route path='/attachments/*' element={<AttachmentsPage />} />
          <Route path='/influence' element={<InfluenceGraph />} />
          <Route path='/message_tree/:message_id' element={<MessageTree />} />
          <Route path='/address_book' element={<AddressBookPage />} />
          <Route path='/ohsnap365' element={<OhSnapPage />} />
          <Route path='/ohsnap365/:username' element={<OhSnapPage />} />
          <Route path='/ohsnap365/:username/:date' element={<OhSnapPage />} />
          <Route path='/admin' element={<AdminUserListPage />} />
          <Route path='/login' element={<LoginPage />} />
        </Routes>
      </motion.div>
    </React.Suspense>
  );
};
